import { TrialMessageUsage } from "@app/components/app/TrialMessageUsage";
import { AdminSettingsSearchNav } from "@app/components/navigation/AdminSettingsSearchNav";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { FREE_TRIAL_PHONE_PLAN_CODE } from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import {
  LayoutLeft,
  NavigationList,
  NavigationListCompactLabel,
  NavigationListItem,
  XClose,
} from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import React from "react";

interface NavigationSidebarProps {
  children: React.ReactNode;
  owner: WorkspaceType;
  subNavigation?: SidebarNavigation[] | null;
  subscription: SubscriptionType;
  isMobile?: boolean;
  onHideContext: () => void;
}

/** Context has one owner; the rail and profile are rendered once by the app shell. */
export const NavigationSidebar = React.forwardRef<
  HTMLDivElement,
  NavigationSidebarProps
>(function NavigationSidebar(
  { owner, subscription, subNavigation, children, isMobile, onHideContext },
  ref
) {
  const { t } = useLingui();
  const router = useAppRouter();
  const { hasFeature } = useFeatureFlags();
  const isAdminSection = subNavigation?.some(
    (nav) =>
      nav.id === "organization" || nav.id === "platform" || nav.id === "spend"
  );
  const path = router.asPath;
  const title = isAdminSection
    ? t`Settings`
    : path.includes("/spaces")
      ? t`Regions`
      : path.includes("/builder/agents")
        ? t`Agents`
        : path.includes("/builder/skills")
          ? t`Skills`
          : path.includes("/pods")
            ? t`Collaborative work`
            : t`Conversations`;
  const CloseIcon = isMobile ? XClose : LayoutLeft;
  return (
    <div ref={ref} className="flex min-h-0 min-w-0 grow flex-col">
      <div className="az-context-heading">
        <span className="truncate">{title}</span>
        <button
          type="button"
          className="az-context-toggle"
          onClick={onHideContext}
          aria-label={
            isMobile ? t`Close navigation` : t`Hide context navigation`
          }
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="az-context-content">
        {subNavigation && (
          <div className="mx-sidebar-side-spacing py-3">
            {isAdminSection ? (
              <AdminSettingsSearchNav
                subNavigation={subNavigation}
                hasFeature={hasFeature}
              />
            ) : (
              <NavigationList>
                {subNavigation.map((nav) => (
                  <React.Fragment key={nav.id}>
                    {nav.label && (
                      <NavigationListCompactLabel label={nav.label} />
                    )}
                    {nav.menus
                      .filter(
                        (menu) =>
                          !menu.featureFlag || hasFeature(menu.featureFlag)
                      )
                      .map((menu) => (
                        <NavigationListItem
                          key={menu.id}
                          selected={menu.current}
                          disabled={menu.disabled}
                          label={menu.label}
                          icon={menu.icon}
                          href={menu.href}
                          target={menu.target}
                        />
                      ))}
                  </React.Fragment>
                ))}
              </NavigationList>
            )}
          </div>
        )}
        <div className="flex min-h-0 grow flex-col">{children}</div>
      </div>
      {subscription.plan.code === FREE_TRIAL_PHONE_PLAN_CODE && (
        <div className="mx-3 mb-3">
          <TrialMessageUsage isAdmin={isAdmin(owner)} workspaceId={owner.sId} />
        </div>
      )}
    </div>
  );
});
